import React from 'react';
import { PhoneForwarded, CalendarDays, Clock, BarChart3, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface TodaysOverviewProps {
  hasData: boolean;
  onNavigate?: (screen: string) => void;
  minutesTotal?: number;
}
export function TodaysOverview({
  hasData,
  onNavigate,
  minutesTotal = 180
}: TodaysOverviewProps) {
  const {
    isLight
  } = useTheme();
  const minutesUsed = Math.min(34, minutesTotal);
  return <div className="space-y-3" data-id="element-402" data-mp="30yhq4379h73z">
      <h3 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-403" data-mp="30xq15b8667mo">
        Today's Overview
      </h3>
      {hasData ? <div className="space-y-3" data-id="element-404" data-mp="3aoukso8signu">
          {/* Calls & Meetings row */}
          <div className="grid grid-cols-2 gap-3" data-id="element-405" data-mp="2d8x2v842rnow">
            <button onClick={() => onNavigate?.('calls')} className={`flex items-center gap-3 p-3.5 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-406" data-mp="2d85kiyzhnmgk">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-blue-500 bg-blue-50' : 'text-blue-400 bg-blue-500/10'}`} data-id="element-407" data-mp="3jtarjlovvawl">
                <PhoneForwarded className="w-5 h-5" data-id="element-408" data-mp="3a0lkd30d8j72" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-409" data-mp="35569mjxnn3ev">
                <span className={`text-xl font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-410" data-mp="304ibleb0dmb5">
                  12
                </span>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-411" data-mp="2r3twshdsjhx7">
                  Calls Handled
                </span>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-412" data-mp="pbxg6tcrnhvk" />
            </button>

            <button onClick={() => onNavigate?.('calendar')} className={`flex items-center gap-3 p-3.5 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-413" data-mp="18f97bd85cz9j">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-purple-500 bg-purple-50' : 'text-purple-400 bg-purple-500/10'}`} data-id="element-414" data-mp="1s4jkajgid63y">
                <CalendarDays className="w-5 h-5" data-id="element-415" data-mp="1i7opaajg1nuf" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-416" data-mp="1n5q4gkdr130m">
                <span className={`text-xl font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-417" data-mp="1n4ow4no4auml">
                  4
                </span>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-418" data-mp="brmf5ri3w31w">
                  Meetings Booked
                </span>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-419" data-mp="3vdboduglgngg" />
            </button>
          </div>

          {/* Minutes Used — full width with progress bar */}
          <button onClick={() => onNavigate?.('profile')} className={`w-full p-4 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-420" data-mp="3go79on90d1d3">
            <div className="flex items-center justify-between mb-2.5" data-id="element-421" data-mp="3lna3tcng9n6a">
              <div className="flex items-center gap-2.5" data-id="element-422" data-mp="3llyxp66j83c0">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-amber-500 bg-amber-50' : 'text-amber-400 bg-amber-500/10'}`} data-id="element-423" data-mp="2xwwjk8b3wsm9">
                  <Clock className="w-4.5 h-4.5" data-id="element-424" data-mp="36nodnpmn9w0k" />
                </div>
                <div data-id="element-425" data-mp="3blzmbx17e0h7">
                  <span className={`text-sm font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-426" data-mp="3blg7wqarhu7x">
                    Minutes Used
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-427" data-mp="3pcuray8kzxfi">
                <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-428" data-mp="3pefm8oyt5idw">
                  {minutesUsed}
                </span>
                <span className={`text-xs ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-429" data-mp="35wi619autxm">
                  / {minutesTotal} min
                </span>
                <ChevronRight className={`w-3.5 h-3.5 ml-1 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-430" data-mp="24yc8iiptmc27" />
              </div>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/10'}`} data-id="element-431" data-mp="r19lk71um21m">
              <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{
            width: `${minutesUsed / minutesTotal * 100}%`
          }} data-id="element-432" data-mp="m7wwxwx65v8v" />
            </div>
          </button>
        </div> : <div className={`rounded-2xl p-6 flex flex-col items-center text-center ${isLight ? 'bg-white/70 border border-slate-100' : 'bg-white/[0.03] border border-white/5'}`} data-id="element-433" data-mp="19x7znjy2xzpn">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-434" data-mp="110qpaixji5uy">
            <BarChart3 className={`w-6 h-6 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-435" data-mp="3lpwip59dwl4v" />
          </div>
          <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-436" data-mp="3goh0dkylcsq6">
            No activity yet
          </p>
          <p className={`text-xs leading-relaxed max-w-[220px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-437" data-mp="3n4zag7zdf2mi">
            Once Vexu.ai starts handling calls, your stats will appear here.
          </p>
        </div>}
    </div>;
}